import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IThongTinDiaPhuong, defaultValue } from 'app/shared/model/thong-tin-dia-phuong.model';

const initialState: EntityState<IThongTinDiaPhuong> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/thong-tin-dia-phuongs';
const apiSearchUrl = 'api/_search/thong-tin-dia-phuongs';

// Actions

export const searchEntities = createAsyncThunk('thongTinDiaPhuong/search_entity', async ({ query, page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiSearchUrl}?query=${query}`;
  return axios.get<IThongTinDiaPhuong[]>(requestUrl);
});

export const getEntities = createAsyncThunk('thongTinDiaPhuong/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}?cacheBuster=${new Date().getTime()}`;
  return axios.get<IThongTinDiaPhuong[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'thongTinDiaPhuong/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IThongTinDiaPhuong>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'thongTinDiaPhuong/create_entity',
  async (entity: IThongTinDiaPhuong, thunkAPI) => {
    const result = await axios.post<IThongTinDiaPhuong>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'thongTinDiaPhuong/update_entity',
  async (entity: IThongTinDiaPhuong, thunkAPI) => {
    const result = await axios.put<IThongTinDiaPhuong>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'thongTinDiaPhuong/partial_update_entity',
  async (entity: IThongTinDiaPhuong, thunkAPI) => {
    const result = await axios.patch<IThongTinDiaPhuong>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'thongTinDiaPhuong/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IThongTinDiaPhuong>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const ThongTinDiaPhuongSlice = createEntitySlice({
  name: 'thongTinDiaPhuong',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, searchEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, searchEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ThongTinDiaPhuongSlice.actions;

// Reducer
export default ThongTinDiaPhuongSlice.reducer;
